/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';

import { RemoteActionDaoService } from '../remote-action-dao.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { AppConfig } from 'src/app/app.config';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionDbTableDefinition } from './remote-action-db-table-definition';

/**
 * DataAccessObject for Remote Actions
 */
@Injectable()
export class RemoteActionDbDaoService implements RemoteActionDaoService {
  // LifeCycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private remoteActionTableDefinition: RemoteActionDbTableDefinition,
  ) {}

  // RemoteActionDaoService
  public async addRemoteAction(remoteAction: RemoteAction): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      let remoteActionQuery: Query = {
        statement:
          'INSERT OR REPLACE INTO ' +
          this.remoteActionTableDefinition.tableName +
          '(endpoint, data, cachable, fifo, authenticationRequired, ' +
          ' rawRequestTimestamp, editedRequestTimestamp, executionTimestamp, ' +
          ' shortLabel, shortLabelTranslate, longLabel, longLabelTranslate, afterSendAction, hideInDayProtocol, pi_delayFilesDeletion, filePath)' +
          ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
        parameters: [
          remoteAction.endpoint,
          JSON.stringify(remoteAction.data),
          remoteAction.cachable,
          remoteAction.fifo,
          remoteAction.authenticationRequired,
          remoteAction.rawRequestTimestamp,
          remoteAction.editedRequestTimestamp,
          remoteAction.executionTimestamp,
          remoteAction.shortLabel,
          remoteAction.shortLabelTranslate,
          remoteAction.longLabel,
          remoteAction.longLabelTranslate,
          JSON.stringify(remoteAction.afterSendActions),
          remoteAction.hideInDayProtocol ?? false,
          remoteAction.pi_delayFilesDeletion ?? false,
          remoteAction.filePath
        ],
      };
      await this.dbPersistenceService.executeStatement(db, remoteActionQuery);
    } catch (error) {
      this.logger.log(
        'Error saving remote action to db',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async getRemoteActions(): Promise<RemoteAction[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let remoteActions: RemoteAction[] = [];

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.remoteActionTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        remoteActions.push(
          new RemoteAction({
            id: row['id'],
            endpoint: row['endpoint'],
            data: JSON.parse(row['data']),
            cachable: row['cachable'] === 1,
            fifo: row['fifo'] === 1,
            authenticationRequired: row['authenticationRequired'] === 1,
            rawRequestTimestamp: row['rawRequestTimestamp'],
            editedRequestTimestamp: row['editedRequestTimestamp'],
            executionTimestamp: row['executionTimestamp'],
            shortLabel: row['shortLabel'],
            shortLabelTranslate: row['shortLabelTranslate'] === 1,
            longLabel: row['longLabel'],
            longLabelTranslate: row['longLabelTranslate'] === 1,
            afterSendActions: JSON.parse(row['afterSendAction']),
            hideInDayProtocol: row['hideInDayProtocol'] === 1,
            pi_delayFilesDeletion: row['pi_delayFilesDeletion'] === 1,
            filePath: row['filePath']
          }),
        );
      }

      return remoteActions;
    } catch (error) {
      this.logger.log('Error reading remote action from db', error);
    }
    return [];
  }

  public async getNextRemoteAction(nonFifo: boolean): Promise<RemoteAction> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let remoteAction: RemoteAction;

      let statement =
        'SELECT * FROM ' +
        this.remoteActionTableDefinition.tableName +
        ' LIMIT 1;';
      if (nonFifo) {
        statement =
          'SELECT * FROM ' +
          this.remoteActionTableDefinition.tableName +
          ' WHERE fifo is "false" LIMIT 1;';
      }
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: statement,
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        remoteAction = new RemoteAction({
          id: row['id'],
          endpoint: row['endpoint'],
          data: JSON.parse(row['data']),
          cachable: row['cachable'],
          fifo: row['fifo'] === true || row['fifo'] === 'true' ? true : false,
          authenticationRequired: row['authenticationRequired'],
          rawRequestTimestamp: row['rawRequestTimestamp'],
          editedRequestTimestamp: row['editedRequestTimestamp'],
          executionTimestamp: row['executionTimestamp'],
          shortLabel: row['shortLabel'],
          shortLabelTranslate: row['shortLabelTranslate'],
          longLabel: row['longLabel'],
          longLabelTranslate: row['longLabelTranslate'],
          afterSendActions: JSON.parse(row['afterSendAction']),
          hideInDayProtocol: row['hideInDayProtocol'] === 1,
          pi_delayFilesDeletion: row['pi_delayFilesDeletion'] === 1,
          filePath: row['filePath']
        });
      }

      return remoteAction;
    } catch (error) {
      this.logger.log('Error reading remote action from db', error);
    }
    return undefined;
  }

  public async removeRemoteAction(remoteAction: RemoteAction): Promise<void> {
    let db = await this.dbPersistenceService.getImsDb();

    let statement =
      'DELETE FROM ' +
      this.remoteActionTableDefinition.tableName +
      ' WHERE id = ' +
      remoteAction.id +
      ';';

    await this.dbPersistenceService.executeStatement(db, {
      statement: statement,
      parameters: null,
    });
  }

  public async reset() {
    // On a reset all not sent remote action should be cleared
    await this.clear();
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    await this.dbPersistenceService.truncateTable(
      db,
      this.remoteActionTableDefinition.tableName,
    );
  }
}
